/**
 * The change form's logic (Exception center redesign, Phase 1; the owner, 2026-10-21): every kind of change edits one
 * basket — what the day brings, by variant code — and a scope says how long it holds:
 *   · DAYS        only these days (tomorrow, a date, a range, chosen weekdays): api.apply_customer_change with the whole
 *                 basket as QUANTITY lines (a variant at 0 is paused those days; a new one is added; several at once),
 *                 SKIP/PAUSE when nothing is left, MAKEUP for a make-up's extra units;
 *   · UNTIL       until further notice, from a date: api.hold_customer_basket (Resume brings the earlier items back), or a
 *                 pause until further notice when nothing is left;
 *   · PERMANENT   from a date for good: api.change_customer_variant; nothing left is not a basket but a pause or the
 *                 customer leaving, so the form suggests those instead.
 * Before a day's cut-off the same day may be changed again: the latest basket wins (138600/138700/138800 on the server).
 * Half units are the H bottles (500 ml): half of G is HG, of G+ HG+, of G++ HG++ (the owner, 2026-10-21). Pure: the form
 * and its tests share it.
 */
export type Basket = Record<string, number>;
export type Scope = 'DAYS' | 'UNTIL' | 'PERMANENT';
export type Preset = 'quantity' | 'skip' | 'pause' | 'add' | 'remove' | 'substitute' | 'makeup';

/** The 500 ml bottle of a litre variant, and the litre variant of a 500 ml one. */
export const HALF: Record<string, string> = { G: 'HG', 'G+': 'HG+', 'G++': 'HG++' };
export const WHOLE: Record<string, string> = Object.fromEntries(Object.entries(HALF).map(([a, b]) => [b, a]));

export const clean = (b: Basket): Basket => Object.fromEntries(Object.entries(b).filter(([, n]) => n > 0));
export const isEmpty = (b: Basket) => Object.values(b).every((n) => !n);
export const sameBasket = (a: Basket, b: Basket) => JSON.stringify(Object.entries(clean(a)).sort()) === JSON.stringify(Object.entries(clean(b)).sort());

/** Litres of a variant family (G and HG make one family): "1.5 L" for G 1 + HG 1. */
export function litres(b: Basket, code: string): number | null {
  const whole = WHOLE[code] ?? code;
  const half = HALF[whole];
  if (!half) return null;
  return (b[whole] ?? 0) + (b[half] ?? 0) / 2;
}

/** What the preset makes of the day's basket when it is chosen. */
export function presetBasket(preset: Preset, before: Basket): Basket {
  if (preset === 'skip' || preset === 'pause') return Object.fromEntries(Object.keys(before).map((k) => [k, 0]));
  return { ...before };
}

/** Swap a variant for another, keeping its quantity (tap a bottle → another variant). */
export function swap(b: Basket, from: string, to: string): Basket {
  if (from === to) return b;
  const out: Basket = { ...b, [to]: (b[to] ?? 0) + (b[from] ?? 0) };
  out[from] = 0;
  return out;
}

/** The half-bottle toggle: add one 500 ml of the family, or take it away. */
export function toggleHalf(b: Basket, code: string): Basket {
  const half = HALF[WHOLE[code] ?? code];
  if (!half) return b;
  return { ...b, [half]: (b[half] ?? 0) > 0 ? 0 : 1 };
}

export interface Diff { code: string; before: number; after: number }
export function diff(before: Basket, after: Basket): Diff[] {
  return [...new Set([...Object.keys(before), ...Object.keys(after)])].sort()
    .map((code) => ({ code, before: before[code] ?? 0, after: after[code] ?? 0 }))
    .filter((d) => d.before !== d.after || d.after > 0);
}

/**
 * What the edits are, read from the basket (the owner, 2026-10-22: "the changes made in the details … should select the
 * option in What"): every product at 0 is a skip for one day and a pause for several days or until further notice; one
 * of several products at 0 is "remove a product"; a product the day did not have is "add product" (the ½ bottle too),
 * or "substitute" when a bottle was swapped for it; a product kept but at another quantity is "change quantity". A
 * mixed edit names each (G 1→0 and HG 1→2 is remove + change quantity). Nothing changed names nothing.
 */
export function deriveTypes(before: Basket, after: Basket, swappedTo: readonly string[], manyDays: boolean): Preset[] {
  const had = Object.keys(clean(before));
  if (had.length > 0 && isEmpty(after)) return [manyDays ? 'pause' : 'skip'];
  const out = new Set<Preset>();
  for (const code of new Set([...Object.keys(before), ...Object.keys(after)])) {
    const b = before[code] ?? 0; const a = after[code] ?? 0;
    if (a === b) continue;
    if (b > 0 && a === 0) out.add(had.length > 1 ? 'remove' : manyDays ? 'pause' : 'skip');
    else if (b === 0 && a > 0) out.add(swappedTo.includes(code) ? 'substitute' : 'add');
    else out.add('quantity');
  }
  // A swap is one change, not a removal and an addition.
  if (out.has('substitute')) { out.delete('remove'); out.delete('skip'); out.delete('pause'); }
  const order: Preset[] = ['quantity', 'skip', 'pause', 'add', 'remove', 'substitute', 'makeup'];
  return order.filter((p) => out.has(p));
}

export interface When {
  first: string; last: string | null; weekdays?: number[]; keepDaysOff?: boolean;
  /** 141500: every second day of the range, from the first (the owner, 2026-10-24). */
  everyOther?: boolean;
  /** The first day an agreement change may start on, when `first` is past its cut-off and the CRM manager changes it
   *  anyway (the owner, 2026-10-22): `first` is changed as a day (the late change), the agreement from this day. */
  openFrom?: string;
}
export type Plan =
  | { call: 'apply'; change: Record<string, unknown> }
  | { call: 'hold'; from: string; lines: { variantId: string; quantity: number }[]; firstDay?: Record<string, unknown> }
  | { call: 'permanent'; from: string; lines: { variantId: string; quantity: number }[]; firstDay?: Record<string, unknown> }
  | { call: 'suggest'; why: string }
  | { call: 'none'; why: string };

/** The server call the form makes for a basket, a preset and a scope. `idOf` turns a variant code into its id. */
export function planChange(a: {
  customerId: string; preset: Preset; scope: Scope; before: Basket; after: Basket; when: When;
  reasonCode: string; remark: string | null; idOf: (code: string) => string | undefined;
}): Plan {
  const { customerId, preset, scope, before, after, when, reasonCode, remark, idOf } = a;
  const empty = isEmpty(after);
  const positive = Object.entries(clean(after)).map(([code, quantity]) => ({ variantId: idOf(code) ?? '', quantity }));
  if (positive.some((l) => !l.variantId)) return { call: 'none', why: 'An item is not an active variant.' };
  // A frozen first day (past its cut-off, the CRM manager): that day changes as a day, the agreement from the next open one.
  const late = when.openFrom && when.openFrom > when.first ? when.openFrom : null;
  const firstDay = late ? { kind: 'QUANTITY', customerId, from: when.first, until: when.first, reasonCode, remark,
    lines: [...new Set([...Object.keys(before), ...Object.keys(after)])].map((code) => ({ variantId: idOf(code) ?? '', quantity: after[code] ?? 0 })).filter((l) => l.variantId) } : undefined;
  if (scope === 'PERMANENT') {
    if (empty) return { call: 'suggest', why: 'Nothing is left to deliver: this is a pause until further notice, or the customer leaving.' };
    if (sameBasket(before, after)) return { call: 'none', why: 'Nothing changes.' };
    return { call: 'permanent', from: late ?? when.first, lines: positive, ...(firstDay ? { firstDay } : {}) };
  }
  if (scope === 'UNTIL') {
    if (empty) return { call: 'apply', change: { kind: 'PAUSE', customerId, from: when.first, until: null, reasonCode, remark } };
    if (sameBasket(before, after)) return { call: 'none', why: 'Nothing changes.' };
    return { call: 'hold', from: late ?? when.first, lines: positive, ...(firstDay ? { firstDay } : {}) };
  }
  const base: Record<string, unknown> = { customerId, from: when.first, until: when.last ?? when.first, reasonCode, remark };
  if (when.weekdays?.length) base.weekdays = when.weekdays;
  if (when.everyOther) base.everyOther = true;
  if (empty) return { call: 'apply', change: { ...base, kind: preset === 'pause' ? 'PAUSE' : 'SKIP' } };
  if (preset === 'makeup') {
    const extra = diff(before, after).filter((d) => d.after > d.before).map((d) => ({ variantId: idOf(d.code) ?? '', quantity: d.after - d.before }));
    if (!extra.length) return { call: 'none', why: 'A make-up adds units.' };
    return { call: 'apply', change: { ...base, kind: 'MAKEUP', lines: extra } };
  }
  // An add-on on chosen days (the owner, 2026-10-30: it is allowed after the cut-off too): only the units ADDED are sent, as an ADD. The server puts them on
  // the day's order before the cut-off, and as its own EXTRA order on the round that is not yet loaded after it. Sent as the whole basket (QUANTITY) a product
  // the day does not carry was refused after the cut-off ("HG is not on ... round").
  if (preset === 'add' && !diff(before, after).some((d) => d.after < d.before)) {
    const extra = diff(before, after).filter((d) => d.after > d.before).map((d) => ({ variantId: idOf(d.code) ?? '', quantity: d.after - d.before }));
    if (!extra.length) return { call: 'none', why: 'Nothing changes.' };
    return { call: 'apply', change: { ...base, kind: 'ADD', lines: extra } };
  }
  if (sameBasket(before, after)) return { call: 'none', why: 'Nothing changes.' };
  // The whole basket: every variant of the day before or after, at its new quantity (0 pauses it those days).
  const lines = [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .map((code) => ({ variantId: idOf(code) ?? '', quantity: after[code] ?? 0 })).filter((l) => l.variantId);
  return { call: 'apply', change: { ...base, kind: 'QUANTITY', lines, ...(when.keepDaysOff ? { keepDaysOff: true } : {}) } };
}

/** Is tomorrow past its cut-off? Its clock has passed, or the day was already prepared (a run exists): the setting can move later after that, and the day stays frozen. */
export function isTomorrowCut(state: { tomorrowCutoffAt?: string; runs?: { serviceDate: string }[] } | null | undefined, today: string, nowMs: number): boolean {
  if (!state) return false;
  const d = new Date(`${today}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1);
  const tomorrow = d.toISOString().slice(0, 10);
  if ((state.runs ?? []).some((r) => r.serviceDate === tomorrow)) return true;
  return state.tomorrowCutoffAt ? nowMs > Date.parse(state.tomorrowCutoffAt) : false;
}
